import { Component, type ReactNode } from 'react';
import { useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { Button } from '@/components/common';
import { ROUTES } from '@/routes/routes';
import styles from './ErrorBoundary.module.css';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryProps {
  children?: ReactNode;
  fallback?: ReactNode;
}

// Class component for catching render errors
export class ErrorBoundaryClass extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    // TODO: Send to error tracking service
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <ErrorDisplay 
          error={this.state.error} 
          onRetry={this.handleRetry}
        />
      );
    }
    return this.props.children;
  }
}

// Function component for route errors (used by React Router)
export function ErrorBoundary() {
  const error = useRouteError();
  
  if (isRouteErrorResponse(error)) {
    return (
      <ErrorDisplay
        title={`${error.status} ${error.statusText}`}
        message={error.data?.message || 'An unexpected error occurred'}
      />
    );
  }
  
  if (error instanceof Error) {
    return <ErrorDisplay error={error} />;
  }
  
  return <ErrorDisplay message="An unexpected error occurred" />;
}

interface ErrorDisplayProps {
  error?: Error | null;
  title?: string;
  message?: string;
  onRetry?: () => void;
}

function ErrorDisplay({ error, title, message, onRetry }: ErrorDisplayProps) {
  const errorTitle = title || 'Something went wrong';
  const errorMessage = message || error?.message || 'An unexpected error occurred';
  const isDevelopment = import.meta.env.DEV;
  
  const handleGoHome = () => {
    window.location.href = ROUTES.DASHBOARD;
  };

  return (
    <div className={styles.container} role="alert">
      <div className={styles.icon}>
        <svg width="48" height="48" viewBox="0 0 48 48" fill="currentColor">
          <path d="M24 4C12.96 4 4 12.96 4 24s8.96 20 20 20 20-8.96 20-20S35.04 4 24 4zm2 30h-4v-4h4v4zm0-8h-4V14h4v12z" />
        </svg>
      </div>
      <h1 className={styles.title}>{errorTitle}</h1>
      <p className={styles.message}>{errorMessage}</p>
      {isDevelopment && error && (
        <pre className={styles.errorDetails}>
          {error.message}
          {error.stack && `\n\n${error.stack}`}
        </pre>
      )}
      <div className={styles.actions}>
        {onRetry ? (
          <Button variant="primary" onClick={onRetry}>
            Try Again
          </Button>
        ) : (
          <Button variant="primary" onClick={() => window.location.reload()}>
            Reload Page
          </Button>
        )}
        <Button variant="secondary" onClick={handleGoHome}>
          Go to Dashboard
        </Button>
      </div>
    </div>
  );
}
